<script setup lang="ts">
import { minValue } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'
import { DateTime } from 'luxon'

import { AuxiliarMeta, Form, Travel } from '~/layers/forms/models'
import { minDateValidator, requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Travel
  validationScope?: string
  asCriteria?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  validationScope: Form.travel,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const dates = ref<(Date | null)[]>([])

const { getTravelMaxDurationDate } = useContractConstraints()

const { RemoteConfigurationStore } = stores()

const minReturnDate = computed(() =>
  DateTime.fromJSDate(model.value.from ?? new Date())
    .plus({ day: 1 })
    .toJSDate()
)

const calculatedDays = computed(() => {
  if (!model.value.from || !model.value.to) return 0
  const from = DateTime.fromJSDate(model.value.from).startOf('day')
  const to = DateTime.fromJSDate(model.value.to).startOf('day')
  const days = to.diff(from, 'days').toObject().days ?? 0
  return days < 1 ? Math.ceil(days) : Math.floor(days)
})

const v$ = useValidation(
  () => ({
    argentoInsuranceDestinationId: {
      required: requiredValidator,
    },
    from: {
      required: requiredValidator,
      min: minDateValidator(),
    },
    to: {
      required: requiredValidator,
      min: minDateValidator(minReturnDate.value),
    },
    reason: { required: requiredValidator },
    calculatedDays: { required: requiredValidator, min: minValue(1) },
    /* insuredBirthDate: { required: requiredValidator }, */
  }),
  model,
  {
    $scope: props.validationScope,
    $registerAs: Form.travel,
  }
)

watch(
  () => model.value.from,
  (n) => {
    if (!model.value.to) return
    if (
      !n ||
      Math.floor(
        DateTime.fromJSDate(model.value.to).diff(DateTime.fromJSDate(n), 'days').toObject().days ??
          0
      ) <= 0
    ) {
      model.value.to = null
    }
  }
)

watch(calculatedDays, (n) => {
  model.value.calculatedDays = n
})

const {
  execute: fetchDestinations,
  data: destinations,
  status: destinationsStatus,
} = useSiteApi('/api/Auxiliares/ByProducto/Destinos', {
  method: 'post',
  body: {
    items: -1,
    page: -1,
    externalServiceSelector: {
      entidadId: RemoteConfigurationStore.value?.producto?.id?.toString(),
      entidadSelector: 'Producto',
    },
  },
  query: computed(() => ({ productoId: RemoteConfigurationStore.value?.producto?.id })),
  transform(input?: SiteApiSchema<'DestinoDispatcherListModel'>) {
    if (input) return input.items
  },
  immediate: true,
})

const onBlurValidation = () => {
  const { reason, argentoInsuranceDestinationId } = model.value
  if (!!reason && !!argentoInsuranceDestinationId) v$.value.$validate()
}

watch(dates, (n) => {
  if (!n || n?.length !== 2) return

  const [from, to] = n

  model.value.from = dates.value[0] ?? null

  const toHasValue = Boolean(to)

  const datesAreDiferent =
    from &&
    to &&
    DateTime.fromJSDate(from).toFormat('dd/mm/yy') !== DateTime.fromJSDate(to).toFormat('dd/mm/yy')

  if (!toHasValue || !datesAreDiferent) dates.value[1] = null

  model.value.to = dates.value[1] ?? null
})

watch(
  () => model.value.argentoInsuranceDestinationId,
  (n, o) => {
    if (!n || n === o) return
    const destintation = destinations.value?.find((item) => item.id === n)
    if (destintation?.id)
      model.value.destination = AuxiliarMeta.load({
        id: destintation.id,
        name: destintation.nombre,
        code: destintation?.codigo,
      })
  }
)
</script>

<template>
  <FormsGlobalContainer
    :identifier="Form.travel"
    :cols="2"
  >
    <FormsGlobalTitle
      text="Información del viaje"
      icon-class="icon-[ph--airplane-tilt-bold]"
    />

    <InputField
      label="Fecha de partida y retorno"
      :error="v$.calculatedDays?.$error"
      :error-message="v$.calculatedDays?.$errors[0]?.$message.toString()"
    >
      <PvDatePicker
        v-model="dates"
        selection-mode="range"
        :manual-input="false"
        :min-date="DateTime.now().plus({ days: 1 }).toJSDate()"
        :max-date="getTravelMaxDurationDate(model.from)"
        hide-on-range-selection
        :number-of-months="$viewport.isGreaterOrEquals('lg') ? 2 : 1"
        :sufix="model.calculatedDays ? { text: `${model.calculatedDays} días` } : undefined"
      />
    </InputField>

    <InputField
      label="Destino"
      :error="v$.argentoInsuranceDestinationId?.$error"
      :error-message="v$.argentoInsuranceDestinationId?.$errors[0]?.$message.toString()"
      :loading="destinationsStatus === 'pending'"
    >
      <InputGroupRetry
        :retry="!destinations?.length && !!model.argentoInsuranceDestinationId"
        @retry="fetchDestinations()"
      >
        <PvSelect
          v-model="model.argentoInsuranceDestinationId"
          :options="destinations ?? []"
          option-value="id"
          option-label="nombre"
          @blur="onBlurValidation()"
        />
      </InputGroupRetry>
    </InputField>

    <InputField
      label="Motivo del viaje"
      :error="v$.reason?.$error"
      :error-message="v$.reason?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.reason"
        option-label="label"
        option-value="code"
        :options="Travel.getTravelReasonTypeList()"
        @blur="onBlurValidation()"
      />
    </InputField>

    <template v-if="asCriteria">
      <slot name="aditional-fields" />
      <InputField label=" ">
        <slot name="action" />
      </InputField>
    </template>
  </FormsGlobalContainer>
</template>
